<template>
  <div class="py-8">
    <router-link to="/settings/data/custom_fields" class="flex items-center justify-start px-2 text-gray-700">
      <vue-feather type="chevron-left" class="h-8" />
      <span class="text-sm font-semibold">Back</span>
    </router-link>

    <Card extra-classes="max-w-xl mx-auto">
      <div class="flex items-center justify-between">
        <header class="font-bold text-lg">Edit Custom Field</header>
        <TButton color="indigo" size="md" @click="updateCustomField">Update</TButton>
      </div>

      <FormKit v-if="loaded" type="form" :actions="false">
        <FormKit v-model="customField.name" type="text" label="Field name" />
        <FormKit
          v-model="customField.key"
          type="text"
          label="Field key"
          :readonly="true"
          :disabled="true"
          help="This is generated automatically and you will use it when referencing this custom field in templates, documents, or other places. You cannot change it because it will break any existing references to this field."
        />
        <FormKit
          v-model="customField.sensitive"
          type="checkbox"
          label="Sensitive"
          :readonly="true"
          :disabled="true"
          help="If this field contains sensitive information (ex. user logins, passwords, or tax information), you should delete this field and create a new one with the sensitive flag enabled."
        />
        <FormKit
          v-model="customField.scopes"
          type="checkbox"
          label="Entity Type"
          :options="fieldScopes"
          :help="
            showPipelineReviewWarning
              ? 'Adding a custom field to Pipeline Reviews will remove our standard three questions that are asked during a Pipeline Review.'
              : null
          "
        />
        <FormKit
          v-if="showAutoCollect"
          v-model="customField.auto_collect_during_onboarding"
          type="checkbox"
          label="Auto collect during teammate onboarding"
          help="When new agents go through the Humaniz onboarding process, do you want us to automatically get this from them?"
        />
        <FormKit v-model="customField.field_type" type="select" label="Input type" :options="fieldTypeOptions" />
        <FormKit
          v-if="customField.field_type === 'single_option' || customField.field_type === 'multiple_options'"
          v-model="customField.options"
          type="textarea"
          label="Options"
          rows="4"
        />
      </FormKit>

      <TButton color="red" size="md" class="flex flex-row gap-2 mt-8" @click="showDelete = true"
        ><vue-feather type="trash" size="16" /> Delete</TButton
      >
    </Card>
    <Modal
      v-if="showDelete"
      ref="modal"
      title="Delete custom field"
      body="Are you sure you want to delete this custom field? Deleting a custom field is permanent and will delete any data associated with this field or associated teammates, candidates, etc."
      action="Delete"
      action-color="red"
      @close-modal="showDelete = false"
      @handle-action="deleteCustomField"
    />
  </div>
</template>

<script>
import Card from '../../Card/index.vue';
import Modal from '../../shared/Modal.vue';
import { Api } from '../../../api/index.ts';

export default {
  components: {
    Card,
    Modal,
  },
  data() {
    return {
      loaded: false,
      showDelete: false,
      customField: {
        name: '',
        key: '',
        field_type: '',
        auto_collect_during_onboarding: false,
        options: [],
        scopes: [],
        sensitive: null,
      },
    };
  },
  computed: {
    fieldScopes() {
      return { 'contact:candidate': 'Candidate', 'contact:teammate': 'Teammate', pipeline_review: 'Pipeline Review' };
    },
    showAutoCollect() {
      return this.customField.scopes.includes('contact:teammate');
    },
    showPipelineReviewWarning() {
      return this.customField.scopes.includes('pipeline_review');
    },
    fieldTypeOptions() {
      return [
        {
          value: 'short_text',
          label: 'Text',
        },
        {
          value: 'single_option',
          label: 'Single option',
        },
        {
          value: 'multiple_options',
          label: 'Multiple options',
        },
        {
          value: 'date',
          label: 'Date',
        },
        {
          value: 'number',
          label: 'Number',
        },
        {
          value: 'percentage',
          label: 'Percentage',
        },
        {
          value: 'currency',
          label: 'Currency',
        },
        {
          value: 'rich_text',
          label: 'Rich text',
        },
        {
          value: 'address',
          label: 'Address',
        },
      ];
    },
  },
  watch: {
    customField: {
      handler(newValue, oldValue) {
        if (!newValue.scopes.includes('contact:teammate')) {
          newValue.auto_collect_during_onboarding = false;
        }
      },
      deep: true,
    },
  },
  async mounted() {
    await this.fetchCustomField();
  },
  methods: {
    async fetchCustomField() {
      const id = this.$route.params.id;
      const { record } = await Api.CustomFields.getCustomField({ id });
      this.customField = {
        ...record,
        options: record.options.join('\n'),
      };
      this.loaded = true;
    },
    async updateCustomField() {
      const id = this.$route.params.id;
      const body = {
        ...this.customField,
        options: this.customField.options.split('\n'),
      };

      const data = await Api.CustomFields.updateCustomField({ id, body });

      if (data.status != 'error') {
        await this.$router.push('/settings/data/custom_fields');
        this.$store.commit('general/showAlert', {
          alertType: 'success',
          alertTitle: 'Custom Field Updated',
          alertDescription: 'Your custom field was successfully updated.',
        });
      } else {
        this.$store.commit('general/showAlert', {
          alertType: 'error',
          alertTitle: 'Custom Field Update Error',
          alertDescription: data.message,
        });
      }
    },
    async deleteCustomField() {
      const id = this.$route.params.id;
      await Api.CustomFields.deleteCustomField({ id });

      await this.$router.push('/settings/data/custom_fields');
      this.$store.commit('general/showAlert', {
        alertType: 'success',
        alertTitle: 'Custom Field Deleted',
        alertDescription: 'Your custom field was successfully deleted.',
      });
    },
  },
};
</script>

<style></style>
